import { chakra } from '@chakra-ui/system';
import React, { useCallback, useMemo } from 'react';

import { type InputProps as ChakraInputProps } from '@frontend/components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

export interface InputProps<OptionType extends CascaderOption>
  extends ChakraInputProps {
  inputRef?: React.Ref<HTMLInputElement>;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalInput = <OptionType extends CascaderOption>({
  inputRef,
}: {
  inputRef?: React.Ref<HTMLInputElement>;
}) => {
  const {
    comboboxApi,
    handleInputKeyDown,
    cascaderStyles,
    inputValue,
    setInputValue,
    getCommonProps,
    selectedPath,
    isSearchMode,
  } = useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomInput = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.INPUT),
    [getComponent],
  );

  const {
    onKeyDown: onInputKeyDown,
    placeholder: _,
    defaultValue: _dV,
    size: _size,
    ...inputProps
  } = comboboxApi.getInputProps();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
    },
    [setInputValue],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      handleInputKeyDown(event);
      onInputKeyDown?.(event);
    },
    [handleInputKeyDown, onInputKeyDown],
  );

  return (
    <CustomInput
      inputRef={inputRef}
      {...inputProps}
      getCommonProps={getCommonProps}
      __css={cascaderStyles.input}
      value={inputValue}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      flexGrow={selectedPath.length > 0 && !isSearchMode ? 0 : undefined}
      className="cascader-input"
    />
  );
};

export const Input = <OptionType extends CascaderOption>({
  inputRef,
  getCommonProps: _,
  ...rest
}: InputProps<OptionType>) => {
  return <chakra.input ref={inputRef} {...rest} />;
};
